import React, { useContext, useEffect, useMemo } from 'react';
import { useState } from 'react';

import { dashboardArrivalNoticeHooks } from '@apis/next/arrivalNoticeApis';
import { dashboardDeliveryRequestApis } from '@apis/next/deliveryRequestApis';
import { dashboardDeliveryRequestLogApis } from '@apis/next/deliveryRequestLogApis';
import { dashboardPdfApis } from '@apis/next/pdfApis';
import { dashboardPreAlertApis } from '@apis/next/preAlertApis';
import {
  DeliveryRequestFormDialogContextValues,
  DeliveryRequestFormDialogContextValuesBase,
  DeliveryRequestFormDialogContext,
} from '@components/admin/booking/helpers';
import { Button } from '@components/Button';
import {
  ActionableDialog,
  ActionableDialogActions,
  ActionableDialogTitle,
} from '@components/Dialog';
import { IndexedFormContainer } from '@components/NextForms/IndexedForm';
import { IndexedLabel } from '@components/NextForms/IndexedForm';
import { TextInput } from '@components/NextForms/TextInput';
import { QueryContainer } from '@components/QueryContainer';
import { BookingInquiryContactDisplay } from '@components/utilities/BookingInquiry/BookingInquiryContact';
import { deliveryDestinationOptions } from '@data/arrivalNotice';
import { bookingInquiryStrings } from '@data/bookingInquiry';
import {
  allTruckTypes,
  deliveryDateTypeOptions,
  deliveryRequestStrings,
  resolveDeliveryRequestInitialOutput,
} from '@data/deliveryRequest';
import { usePatchDeliveryInstruction as usePatchDashboardDeliveryInstruction } from '@hooks/mutation/dashboard/usePatchDeliveryInstruction';
import { useBookingInquiryDetailByD2dNo } from '@hooks/queries/dashboard/useBookingInquiryDetailByD2dNo';
import { useBlobURL } from '@hooks/useBlobURL';
import { useDialogKey } from '@hooks/useDialogKey';
import { useFormUtils } from '@hooks/useFormUtils';
import { DeliveryRequestForm } from '@models/next/DeliveryRequest';
import { DeliveryRequestHelper } from '@models/next/DeliveryRequestHelper';
import { decodeResultEqual, mapDecodeResult } from '@monoid-dev/mobx-zod-form';
import { useForm } from '@monoid-dev/mobx-zod-form-react';
import { makeMuiStyles, useCommonStyles } from '@styles/index';
import { useQueryClient } from '@tanstack/react-query';
import { formStrings } from '@utils/forms';
import { createStrings } from '@utils/local';
import { getFieldLabel } from '@utils/nextForms';
import { id } from '@utils/types';
import { useShallowMemo } from '@utils/useShallowMemo';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { observer } from 'mobx-react';
import { Document, Page } from 'react-pdf';
import { pipe } from 'remeda';

const strings = createStrings({
  ja: {
    cannotCreateDeliveryRequest: `下記の原因で${deliveryRequestStrings.deliveryRequestDocuments}を作成できません：`,
    title: () => ({
      create: `${deliveryRequestStrings.deliveryRequestFile}を作成する`,
      update: `${deliveryRequestStrings.deliveryRequestFile}を編集する`,
    }),
    submit: () => ({
      create: '作成',
      update: 'OK',
    }),
    inputPrompt: '下記情報を入力してください',
    preview: 'プレビュー',
    create: '作成',
    update: '編集',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  form: {
    ...common.flexColumn,
    flexShrink: 1,
    minHeight: 0,
  },
  dialogContent: {
    ...common.flexColumn,
    rowGap: '1rem',
    padding: '1.5rem 100px',
    flexShrink: 1,
    minHeight: 0,
    overflow: 'scroll',
    maxHeight: 550,
  },
  truckTypeAndCountInput: {
    display: 'grid',
    gridTemplateColumns: '2fr 1fr',
    columnGap: 4,
    paddingTop: '0 !important',
  },
  deliveryDateInput: {
    display: 'grid',
    gridTemplateColumns: '2fr 1fr 1fr',
    columnGap: 4,
    paddingTop: '0 !important',
  },
}));

const DeliveryRequestFormDialogContent: React.FC = observer(() => {
  const {
    initialOutput,
    onClose,
    preAlert,
    bookingInquiry,
    deliveryRequest,
    deliveryInstruction,
    setDeliveryInstruction,
    d2dNo,
    type,
  } = useContext(DeliveryRequestFormDialogContext);

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const queryClient = useQueryClient();

  const form = useForm(DeliveryRequestForm, {
    initialOutput,
  });

  const { fields } = form.root;

  const patchDashboardDeliveryInstruction = usePatchDashboardDeliveryInstruction(d2dNo);
  const postPreviewPdf = dashboardPdfApis.usePost('/dashboard/pdf/preview/DeliveryRequest');
  const patchDeliveryRequest = dashboardDeliveryRequestApis.usePatch(
    '/dashboard/delivery-request/:id',
    {
      params: { id: deliveryRequest?.id! },
    },
    {
      onSuccess() {
        queryClient.invalidateQueries(
          dashboardPdfApis.getKeyByPath('get', '/dashboard/pdf/DeliveryRequest/:id'),
        );

        queryClient.invalidateQueries(
          dashboardDeliveryRequestApis.getKeyByPath('get', '/dashboard/delivery-request'),
        );

        queryClient.invalidateQueries(
          dashboardDeliveryRequestApis.getKeyByPath('get', '/dashboard/delivery-request/:id'),
        );

        queryClient.invalidateQueries(
          dashboardDeliveryRequestLogApis.getKeyByPath(
            'get',
            '/dashboard/delivery-request-log/:id',
          ),
        );
      },
    },
  );
  const postDeliveryRequest = dashboardDeliveryRequestApis.usePost('/dashboard/delivery-request');

  const { renderAlertsAndSpinnerModal } = useFormUtils({
    mutations: [
      patchDashboardDeliveryInstruction,
      postPreviewPdf,
      patchDeliveryRequest,
      postDeliveryRequest,
    ],
  });

  const pdfUrl = useBlobURL(postPreviewPdf.data);

  const [step, setStep] = useState(0);

  pipe(
    mapDecodeResult(fields.pickup_address.fields.delivery_destination.decodeResult, id, ''),
    (delivery_destination) =>
      useEffect(() => {
        const resolved = deliveryDestinationOptions.find(
          (option) => option.delivery_destination === delivery_destination,
        );

        fields.pickup_address.fields.delivery_destination_code.setOutput(
          resolved?.delivery_destination_code ?? '',
        );
      }, [delivery_destination]),
  );

  const renderDeliveryDateInput = () => (
    <div className={classes.deliveryDateInput}>
      <div>
        <TextInput field={fields.delivery_date_date} />
      </div>
      <div>
        <TextInput
          field={fields.delivery_date_type}
          inputOptions={{ type: 'choose', options: deliveryDateTypeOptions }}
        />
      </div>
      {decodeResultEqual(fields.delivery_date_type.decodeResult, 'TIME') && (
        <div>
          <TextInput field={fields.delivery_date_time} type="time" />
        </div>
      )}
    </div>
  );

  const renderPickupAddressInput = () => {
    const { fields: pickupAddressFields } = fields.pickup_address;

    const isOtherPickupAddress = decodeResultEqual(
      pickupAddressFields.delivery_destination.decodeResult,
      'その他',
    );

    return (
      <>
        <IndexedLabel index={7} field={pickupAddressFields.delivery_destination} />
        <div>
          <TextInput
            field={pickupAddressFields.delivery_destination}
            inputOptions={{
              type: 'choose',
              options: deliveryDestinationOptions.map((d) => ({
                label: d.delivery_destination,
                value: d.delivery_destination,
              })),
            }}
          />
        </div>

        {isOtherPickupAddress && (
          <>
            <IndexedLabel field={pickupAddressFields.repository} />
            <div>
              <TextInput
                field={pickupAddressFields.repository}
                inputProps={{ autoComplete: 'organization' }}
              />
            </div>

            <IndexedLabel field={pickupAddressFields.address} />
            <div>
              <TextInput
                field={pickupAddressFields.address}
                inputProps={{ autoComplete: 'address' }}
              />
            </div>

            <IndexedLabel field={pickupAddressFields.tel} />
            <div>
              <TextInput field={pickupAddressFields.tel} inputProps={{ autoComplete: 'tel' }} />
            </div>

            <IndexedLabel field={pickupAddressFields.fax} />
            <div>
              <TextInput field={pickupAddressFields.fax} inputProps={{ autoComplete: 'tel' }} />
            </div>

            <IndexedLabel field={pickupAddressFields.principal_name} />
            <div>
              <TextInput
                field={pickupAddressFields.principal_name}
                inputProps={{ autoComplete: 'name' }}
              />
            </div>
          </>
        )}
      </>
    );
  };

  const renderFormStep0 = () => (
    <IndexedFormContainer>
      <div className={classes.colSpanFull}>{renderAlertsAndSpinnerModal()}</div>
      <div className={clsx(classes.middleText, classes.textGray700, classes.colSpanFull)}>
        {strings.inputPrompt}
      </div>

      <IndexedLabel index={1} label={deliveryRequestStrings.deliveryRequestNo} />
      <div role="data">{deliveryRequest?.delivery_request_no || '-'}</div>

      <IndexedLabel index={2} label={deliveryRequestStrings.d2dNo} />
      <div role="data">{d2dNo}</div>

      <IndexedLabel index={3} label={bookingInquiryStrings.consignee} />
      <div role="data">{bookingInquiry?.consignee?.company_name}</div>

      <IndexedLabel index={4} field={fields.pickup_date} />
      <div>
        <TextInput field={fields.pickup_date} />
      </div>

      <IndexedLabel index={5} field={fields.delivery_date_date} />
      {renderDeliveryDateInput()}

      <IndexedLabel index={6} label={deliveryRequestStrings.pod} />
      <div role="data">{preAlert?.pod}</div>

      {renderPickupAddressInput()}

      <IndexedLabel index={8} label={bookingInquiryStrings.receiver} />
      <div role="data">
        <BookingInquiryContactDisplay
          contactType="receiver"
          contact={deliveryInstruction?.receiver ?? bookingInquiry?.receiver}
          style={{ width: 400 }}
          classes={{
            contactDisplay: classes.middleTextImportant,
          }}
        />
      </div>

      <IndexedLabel index={9} label={deliveryRequestStrings.truckTypeAndCount} />
      <div className={classes.truckTypeAndCountInput}>
        <div>
          <TextInput
            field={fields.truck_type}
            inputOptions={{
              type: 'choose',
              options: allTruckTypes.map((t) => ({ label: t, value: t })),
            }}
            inputProps={{ placeholder: deliveryRequestStrings.truckType }}
          />
        </div>
        <div>
          <TextInput
            field={fields.truck_count}
            inputProps={{ placeholder: deliveryRequestStrings.truckCount }}
          />
        </div>
      </div>

      <IndexedLabel index={10} label={deliveryRequestStrings.cargoQuantity} />
      <div role="data">
        {preAlert?.quantity || '-'} {preAlert?.packages.toUpperCase() || 'PKG'}
      </div>

      <IndexedLabel index={11} label={deliveryRequestStrings.cargoWeight} />
      <div role="data">{preAlert?.gross_weight || '-'} KGS</div>

      <IndexedLabel index={12} label={deliveryRequestStrings.cargoMeasurement} />
      <div role="data">{preAlert?.measurement || '-'} CBM</div>

      <IndexedLabel index={13} field={fields.memo} />
      <div>
        <TextInput
          field={fields.memo}
          multiline
          inputProps={{ placeholder: getFieldLabel(fields.memo) }}
        />
      </div>

      <IndexedLabel index={14} label={deliveryRequestStrings.doLessNo} />
      <div role="data">-</div>

      <IndexedLabel index={15} label={deliveryRequestStrings.moveOutNo} />
      <div role="data">-</div>
    </IndexedFormContainer>
  );

  const renderFormStep1 = () => (
    <>
      <div className={classes.flexColumnCenter}>
        {renderAlertsAndSpinnerModal()}

        {pdfUrl && (
          <Document
            file={pdfUrl}
            loading={
              <div className={classes.flexColumnCenter} style={{ height: 550 }}>
                Loading...
              </div>
            }
          >
            <Page width={900} pageNumber={1} />
            <Page width={900} pageNumber={2} />
          </Document>
        )}
      </div>
    </>
  );

  return (
    <>
      <ActionableDialogTitle textAlign="center">{strings.title()[type]}</ActionableDialogTitle>

      <form
        className={classes.form}
        {...form.bindForm({
          async onSubmit(values) {
            const postDeliveryRequestValues = DeliveryRequestHelper.mapFormToPost(values, {
              d2d_no: d2dNo,
            });

            if (step === 0) {
              // Display both in-db & not-in-db data
              await postPreviewPdf.mutateAsync({
                ...postDeliveryRequestValues,
                delivery_instruction: deliveryInstruction!,
                booking_inquiry: bookingInquiry!,
                pre_alert: preAlert!,
                partner: deliveryRequest?.partner,
                delivery_request_no: deliveryRequest
                  ? deliveryRequest.delivery_request_no
                  : `${d2dNo}YKXX`,
                do_less_no: '',
                move_out_no: '',
                delivery_info: deliveryRequest?.delivery_info,
                created_at: dayjs().format(),
                updated_at: dayjs().format(),
              });
              setStep(step + 1);
            } else if (step === 1) {
              if (type === 'update') {
                await patchDeliveryRequest.mutateAsync({
                  ...postDeliveryRequestValues,
                });
              } else if (type === 'create') {
                const updated = await patchDashboardDeliveryInstruction.mutateAsync({
                  status_name: '確認済み',
                });

                const { id } = await postDeliveryRequest.mutateAsync({
                  ...postDeliveryRequestValues,
                });

                // If not so, the delivery request will not be reflected.
                setDeliveryInstruction!({
                  ...updated,
                  delivery_requests: [...updated.delivery_requests, { id }],
                });
              }

              onClose();
            }
          },
        })}
      >
        <div className={classes.dialogContent}>{[renderFormStep0, renderFormStep1][step]?.()}</div>

        <ActionableDialogActions justifyContent="center">
          <Button
            color="primary"
            size="small"
            shape="round"
            type="button"
            text={formStrings.prev}
            style={{ width: 90 }}
            onClick={() => {
              if (step === 0) {
                onClose();
              } else {
                setStep(step - 1);
                postDeliveryRequest.reset();
              }
            }}
          />

          <Button
            color="primary"
            size="small"
            shape="round"
            type="submit"
            text={step === 0 ? strings.preview : strings.submit()[type]}
            style={{ width: 90 }}
          />
        </ActionableDialogActions>
      </form>
    </>
  );
});

export interface DeliveryRequestFormDialogProps
  extends DeliveryRequestFormDialogContextValuesBase {}

export const DeliveryRequestFormDialog: React.FC<DeliveryRequestFormDialogProps> = (props) => {
  const { type, open, onClose, d2dNo, deliveryInstruction, deliveryRequest } = props;

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const dialogContentKey = useDialogKey(open);

  const bookingInquiry = useBookingInquiryDetailByD2dNo(d2dNo);
  const preAlert = dashboardPreAlertApis.useGet('/dashboard/pre-alert/:d2d_no', {
    params: {
      d2d_no: d2dNo,
    },
  });
  const arrivalNotice = dashboardArrivalNoticeHooks.useGet(
    '/dashboard/arrival-notice/:id',
    {
      params: { id: preAlert.data?.arrival_notice_id! },
    },
    {
      enabled: !!preAlert.data?.arrival_notice_id,
    },
  );

  const maybeInitialValues = useMemo(() => {
    if (type === 'create') {
      if (preAlert.data) {
        return resolveDeliveryRequestInitialOutput(
          preAlert.data,
          arrivalNotice.data,
          deliveryInstruction,
        );
      }
      return undefined;
    } else {
      return {
        _tag: 'Right',
        right: DeliveryRequestHelper.mapListItemToForm(deliveryRequest!),
      } as const;
    }
  }, [preAlert.data, arrivalNotice.data, deliveryInstruction]);

  const contextValue = useShallowMemo(
    (): DeliveryRequestFormDialogContextValues => ({
      ...props,
      initialOutput: maybeInitialValues?._tag === 'Right' ? maybeInitialValues?.right : undefined,
      preAlert: preAlert.data,
      bookingInquiry: bookingInquiry.data,
    }),
  );

  const renderErrors = () => {
    if (maybeInitialValues?._tag === 'Left') {
      return (
        <>
          <div
            className={clsx(classes.middleText, classes.textGray500)}
            style={{ padding: '2rem' }}
          >
            {strings.cannotCreateDeliveryRequest}

            <ul>
              {maybeInitialValues?.left.messages.map((m, i) => (
                <li key={i}>{m}</li>
              ))}
            </ul>

            <Button
              color="primary"
              style={{ width: 100, marginTop: 50 }}
              text="OK"
              size="small"
              onClick={onClose}
            />
          </div>
        </>
      );
    }
  };

  return (
    <DeliveryRequestFormDialogContext.Provider value={contextValue}>
      <ActionableDialog open={open} onClose={onClose} size="lg" source={import.meta.url}>
        <QueryContainer
          queries={[
            preAlert,
            {
              ...arrivalNotice,
              isSuccess: arrivalNotice.isSuccess || arrivalNotice.fetchStatus === 'idle',
            },
            bookingInquiry,
          ]}
        >
          {maybeInitialValues?._tag === 'Left' ? (
            renderErrors()
          ) : (
            <DeliveryRequestFormDialogContent key={dialogContentKey} />
          )}
        </QueryContainer>
      </ActionableDialog>
    </DeliveryRequestFormDialogContext.Provider>
  );
};
